/* eslint-disable no-param-reassign */
import { createAction, createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { findLastIndex, sortBy, unionBy } from 'lodash';
import errorParser from 'utils/errorHelpers';
import { DEFAULT_SCALE_FACTOR } from 'v2/components/viewer/constants';

import { getActionScreenshotUrl } from '@/_utils/actionHelpers/actionHelpers';

import actionsService from '../services/actionsService';
import scribeFileService from '../services/scribeFileService';
import {
  addCreatedStackToActions,
  deleteSectionAndContents,
  getSectionContents,
  getTitleByActionKindOrType,
  optimisticStackUpdate,
  unstackActions,
  updateStackActions
} from '../utils/actionHelpers';

const DEFAULT_ACTIONS_PER_PAGE = 20;

export const getActions = createAsyncThunk(
  'actions/getActions',
  async ({
    scribeDocumentId,
    skip,
    limit,
    actionIds,
    authenticated,
    fetchUntilElementWithHash,
    version_id = null,
    clearActions = false
  }) => {
    if (authenticated === undefined) {
      authenticated = true;
    }

    const retrievedActions = await actionsService.getActions(
      scribeDocumentId,
      skip,
      limit || DEFAULT_ACTIONS_PER_PAGE,
      actionIds || [],
      fetchUntilElementWithHash,
      authenticated,
      version_id
    );

    return { retrievedActions, clearActions };
  }
);

// delete single scribe file action/section
export const deleteScribeFileAction = createAsyncThunk('actions/deleteScribeFileAction', async (actionId) => {
  await scribeFileService.deleteAction(actionId);
  return actionId;
});

export const deleteEntireSection = createAsyncThunk('actions/deleteEntireSection', async ({ sectionId, contentIds }) => {
  await scribeFileService.deleteAction(sectionId);

  if (contentIds) {
    await Promise.all(contentIds.map((actionId) => scribeFileService.deleteAction(actionId)));
  }

  return { sectionId, contentIds };
});

export const updateAction = createAsyncThunk('actions/updateAction', async () => {
  // This here so the dispatch events are triggered elsewhere. This thunk should not be used directly.
  throw new Error('Should make use of the action update mutation instead.');
});

// select/deselect action/section
export const selectedActionChange = createAction('actions/selectedActionChange', (actionId, selected) => {
  return {
    payload: { actionId, selected }
  };
});

export const deselectAllActions = createAction('actions/deselectAllActions');

export const selectUpToAction = createAction('actions/selectUpToAction', (actionId) => {
  return {
    payload: { actionId }
  };
});

export const selectAllActions = createAction('actions/selectAllActions', (selected = false) => {
  return {
    payload: selected
  };
});

export const selectMultipleActions = createAction('actions/selectMultipleActions');

// delete stacked actions -> "unstack"
export const deleteStackedActions = createAsyncThunk('actions/deleteStackedActions', async ({ stack }) => {
  const res = await scribeFileService.deleteStack(stack.id);

  return { stack };
});

export const deleteAutoStackedActions = createAsyncThunk('actions/deleteAutoStackedActions', async (documentId) => {
  await scribeFileService.deleteAutoStackedActions(documentId);
  return {};
});

// stack selected actions
export const stackSelectedActions = createAsyncThunk(
  'actions/stackSelectedActions',
  async ({ scribeDocumentId, actions, actionIdsToStack }) => {
    const selectedActions = actions.filter((action) => actionIdsToStack.includes(action.id));
    const selectedSections = actions.filter((action) => actionIdsToStack.includes(action.id) && action.kind === 'section');
    const firstStack = selectedActions.find((action) => action.kind === 'stack');

    let stack;

    if (firstStack !== undefined) {
      // update
      stack = await scribeFileService.updateStack(firstStack.id, actionIdsToStack);
    } else {
      // create
      stack = await scribeFileService.stackSelectedActions(scribeDocumentId, actionIdsToStack);
    }
    return { stack, actionIdsToStack, updatedStack: firstStack, selectedSections };
  }
);

export const autoStackDocument = createAsyncThunk('actions/autoStackDocument', async ({ scribeDocumentId }) => {
  await scribeFileService.stackSelectedActions(scribeDocumentId);
});

// delete multiple selected actions/sections
export const deleteSelectedActions = createAsyncThunk('actions/deleteSelectedActions', async ({ scribeDocumentId, actions }) => {
  const actionIdsToDelete = actions
    .filter((action) => action.selected)
    .map((action) => action.id)
    .filter((id) => id !== 'newActionPlaceholderId'); // https://cursive.sentry.io/issues/4194957370/?alert_rule_id=2712942&alert_type=issue&notification_uuid=1189bfb7-cfaa-4de4-aaa0-1bcce2fc4cda&project=5217394

  await scribeFileService.deleteMultipleActions(scribeDocumentId, actionIdsToDelete);
  return actionIdsToDelete;
});

// add new action in scribe file
export const scribeFileAddAction = createAsyncThunk('actions/addAction', async (data) => {
  const { documentId, kind, instructionType, addAfterActionId, externalScreenshotUrl } = data;
  const newAction = await scribeFileService.addAction(documentId, kind, instructionType, addAfterActionId, externalScreenshotUrl);

  return { newAction, addAfterActionId };
});

// update actions in scribe file
export const scribeFileUpdateActions = createAction('actions/scribeFileUpdateActions', (updatedActions) => {
  return {
    payload: { updatedActions }
  };
});

export const fetchSingleAction = createAsyncThunk('actions/fetchSingleAction', async (actionId) => {
  return scribeFileService.fetchAction(actionId);
});

export const addLegacyComment = createAsyncThunk('actions/addLegacyComment', async ({ commentedActionId, content, commentUserId }) => {
  const newComment = await actionsService.addLegacyComment(commentedActionId, content, commentUserId);

  return newComment;
});

export const deleteCommentFromAction = createAsyncThunk(
  'actions/deleteComment',
  async ({ commentedActionId, commentId, deletionToken }) => {
    await actionsService.deleteComment(commentId, deletionToken);
    return { commentedActionId, commentId };
  }
);

export const reorderActions = createAsyncThunk('actions/reorderActions', async ({ scribeDocumentId, actionsOrder }) => {
  const result = await actionsService.reorderActions(scribeDocumentId, actionsOrder);

  return result.actions;
});

export const collapseSection = createAction('sections/collapseSection', (collapsedSectionId) => {
  return {
    payload: { collapsedSectionId }
  };
});

export const expandSection = createAction('sections/expandSection', (expandedSectionId) => {
  return {
    payload: { expandedSectionId }
  };
});

export const clearUnstackingErrors = createAction('actions/clearUnstackingErrors', (expandedSectionId) => {
  return {};
});

export const clearDeletingErrors = createAction('actions/clearDeletingErrors', (expandedSectionId) => {
  return {};
});

export const scribeFileGetActionsSortOrder = createAsyncThunk('actions/updateActionsSortOrder', async (data) => {
  const { documentId, fromActionId, toActionId } = data;
  const actions = await scribeFileService.getActionsSortOrder(documentId, fromActionId, toActionId);

  return actions;
});

const getEmptyActionForm = () => ({
  isAddScreenshotModalOpen: false,
  screenshotState: { loaded: false, errored: false, uploadStatus: 'uploaded' },
  values: {},
  errors: {}
});

const getDefaultRecorderState = () => ({ recordBeforeActionId: null, showRecorderSelectionModal: false });

const buildActionForm = (action) => ({
  isAddScreenshotModalOpen: false,
  screenshotState: { loaded: false, errored: false, uploadStatus: 'uploaded' },
  values: {
    screenshotUrl: action.user_cropped_screenshot?.url || action.screenshot_url || action.external_screenshot_url,
    screenshotScale: action.default_scale_factor
    // TODO: Make this work without lagging the UI
    // screenshotTranslate: { x: action.drag_translate_x, y: action.drag_translate_y },
  },
  errors: {}
});

/**
 * @param {GeneralActionProps} action
 */
const buildRecorderActionForm = (action) => {
  // screenshot_upload_status: comes from extension
  // screenshot.upload_status: comes mostly from the server
  let uploadStatus = (action.screenshot_upload_status || action.screenshot?.upload_status)?.toLowerCase?.();

  if (uploadStatus === 'success') {
    uploadStatus = 'uploaded';
  }

  return {
    isAddScreenshotModalOpen: false,
    screenshotState: { loaded: false, errored: false, uploadStatus },
    values: {
      screenshotUrl: getActionScreenshotUrl(action),
      screenshotScale: DEFAULT_SCALE_FACTOR
    },
    errors: {}
  };
};

const INITIAL_STATE = {
  scribeDocumentId: null,
  visibleActions: [],
  collapsedSections: {},
  hiddenActions: {},
  backupActions: [],
  count: 0,
  autoStackCount: 0,
  countWithoutSections: 0,
  isError: false,
  errorMessage: null,
  isLoading: false,
  isDeletingActions: false,
  isDeletingActionsError: null,
  isUnstackingActions: false,
  isUnstackingActionsError: null,
  initialClientSideActionLoadComplete: false,
  // The use-case for the below is real.
  stepRecorderState: getDefaultRecorderState(),
  actionForms: {
    // // This is used to store the form data for each action.
    // // Only what can be changed in view/edit mode and can be triggered from different places need to be here.
    // actionId: {
    //   isAddScreenshotModalOpen: false,
    //   screenshotState: { loaded: false, errored: false, uploadStatus: 'uploaded' },
    //   // This doesn't have a loading state because we do debounced save every second, so UX would suffer.
    //   values: {
    //     screenshotUrl: 'https://example.com/screenshot.png',
    //     screenshotScale: 0.2, // Refers to the image pixels
    //   },
    //   errors: {
    //     screenshotUrl: ['Invalid URL'],
    //     screenshotScale: ['Invalid scale'],
    //   },
    // }
  }
};

// This function updates actions hiddens based on collapsed property
// of the sections to which they "belong." It relies on the actions
// already being sorted by sort_order.
const updateHidden = (actions, collapsedSectionIds, hiddenActionIds) => {
  let currentSectionCollapsed = false;

  for (let i = 0; i < actions.length; i++) {
    if (actions[i].kind === 'section') {
      if (collapsedSectionIds[actions[i].id]) {
      }
      currentSectionCollapsed = collapsedSectionIds[actions[i].id];
      actions[i] = { ...actions[i], collapsed: currentSectionCollapsed };
    } else if ((currentSectionCollapsed && !actions[i].hidden) || (!currentSectionCollapsed && actions[i].hidden)) {
      actions[i] = { ...actions[i], hidden: currentSectionCollapsed };
      hiddenActionIds[actions[i].id] = currentSectionCollapsed;
    }
  }
  return actions;
};

export const actionsSlice = createSlice({
  name: 'actions',
  initialState: { ...INITIAL_STATE },
  reducers: {
    setActionFormValues: (state, { payload: { actionId, ...values } }) => {
      if (typeof state.actionForms[actionId] !== 'object') {
        state.actionForms[actionId] = getEmptyActionForm();
      }

      // TODO: The translate reset is only here because we're not holding that value in the Redux form at the moment
      delete state.actionForms[actionId].errors.screenshotTranslate;
      Object.keys(values).forEach((key) => {
        // Reset errors on fields changed
        delete state.actionForms[actionId].errors[key];
      });

      Object.assign(state.actionForms[actionId].values, values);
    },
    setActionFormErrors: (state, { payload: { actionId, ...errors } }) => {
      if (typeof state.actionForms[actionId] !== 'object') {
        state.actionForms[actionId] = getEmptyActionForm();
      }
      Object.assign(state.actionForms[actionId].errors, errors);
    },
    setActionFormScreenshotState: (state, { payload: { actionId, ...screenshotState } }) => {
      if (typeof state.actionForms[actionId] !== 'object') {
        state.actionForms[actionId] = getEmptyActionForm();
      }
      Object.assign(state.actionForms[actionId].screenshotState, screenshotState);
    },
    setActionForms: (state, { payload: { actions, overwrite } }) => {
      if (overwrite) {
        state.actionForms = Object.fromEntries(actions.map((item) => [item.id, buildActionForm(item)]));
      } else {
        actions.forEach((action) => {
          if (state.actionForms[action.id]) return;
          state.actionForms[action.id] = buildActionForm(action);
        });
      }
    },
    setNewScreenshotModalOpen: (state, { payload: { actionId, open } }) => {
      state.actionForms[actionId].isAddScreenshotModalOpen = !!open; // TODO: Remove casting once we have types
    },
    setStepRecorderState: (state, { payload }) => {
      if (payload === null) {
        state.stepRecorderState = getDefaultRecorderState();
      } else {
        if (payload.recordAfterActionId) {
          const actionIndex = state.visibleActions.findIndex((action) => action.id === payload.recordAfterActionId);

          delete payload.recordAfterActionId;
          payload.recordBeforeActionId = state.visibleActions[actionIndex + 1]?.id || null;
        }
        Object.assign(state.stepRecorderState, payload);
      }
    },
    /**
     * @param state
     * @param {GeneralActionProps[]} actions
     */
    setRecorderActionForms: (state, { payload: actions }) => {
      actions.forEach((action) => {
        const newForm = buildRecorderActionForm(action);
        const existingForm = state.actionForms[action.id] || null;

        if (existingForm && existingForm.values.screenshotUrl === newForm.values.screenshotUrl) {
          delete newForm.screenshotState;
        }
        state.actionForms[action.id] = { ...existingForm, ...newForm };
      });
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getActions.fulfilled, (state, action) => {
        const { scribeDocumentId } = action.meta.arg;

        if (state.scribeDocumentId !== scribeDocumentId) {
          // in this case, this fufilled action was for a different ID than the
          // most recently requested scribedocumentId, so we ignore it
          //
          // In both of these cases, B is the most recent pending action and the one
          // that should be respected.
          //
          // pending A - sets A scribe doc id
          // pending B - sets B scribe doc id
          // fufilled A <-- will be ignored
          // fufilled B <-- this would be fine
          //
          // pending A - sets A scribe doc id
          // pending B - sets B scribe doc id
          // fufilled B <-- this will be approved
          // fufilled A <-- this will be ignored
          return;
        }

        let merged = unionBy(action.payload.retrievedActions.actions, state.visibleActions, 'id');

        if (action.payload.clearActions) {
          merged = action.payload.retrievedActions.actions;
        }

        state.visibleActions = updateHidden(sortBy(merged, ['sort_order']), state.collapsedSections, state.hiddenActions);

        state.visibleActions.forEach((item) => {
          if (state.actionForms[item.id]) return;
          state.actionForms[item.id] = buildActionForm(item);
        });

        state.count = action.payload.retrievedActions.count;
        state.countWithoutSections = action.payload.retrievedActions.count_without_sections;
        state.autoStackCount = action.payload.retrievedActions.auto_stack_count;
        state.isError = false;
        state.errorMessage = null;
        state.isLoading = false;
        state.initialClientSideActionLoadComplete = true;
      })
      .addCase(getActions.pending, (state, action) => {
        const { scribeDocumentId, skip } = action.meta.arg;

        if (state.scribeDocumentId !== scribeDocumentId || skip === 0) {
          state.visibleActions = [];
          state.collapsedSections = {};
          state.hiddenActions = {};
        }
        state.scribeDocumentId = scribeDocumentId;
        state.isError = false;
        state.errorMessage = null;
        state.isLoading = true;
      })
      .addCase(getActions.rejected, (state, action) => {
        state.isError = true;
        state.errorMessage = errorParser(action.payload, 'actionSlice.getActions');
        state.isLoading = false;
      })
      .addCase(deleteScribeFileAction.pending, (state, action) => {
        const actionId = action.meta.arg;

        state.isDeletingActions = true;
        state.isDeletingActionsError = null;
        state.actionBeingDeleted = state.visibleActions.find((action) => action.id === actionId);
        state.visibleActions = state.visibleActions.filter((action) => action.id !== actionId);
      })
      .addCase(deleteScribeFileAction.rejected, (state) => {
        state.isDeletingActions = false;
        state.actionBeingDeleted = undefined;
      })
      .addCase(deleteScribeFileAction.fulfilled, (state, action) => {
        if (
          state.actionBeingDeleted &&
          state.actionBeingDeleted.kind !== 'section' &&
          state.actionBeingDeleted.instruction_type !== 'tip' &&
          state.actionBeingDeleted.instruction_type !== 'warning'
        ) {
          state.countWithoutSections -= 1;
        }
        state.actionBeingDeleted = undefined;
        state.count -= 1;
        state.isDeletingActions = false;
      })
      .addCase(deleteEntireSection.rejected, (state) => {
        state.isDeletingActions = false;
        state.isDeletingActionsError = 'Error while trying to delete entire section';
      })
      .addCase(deleteEntireSection.pending, (state) => {
        state.isDeletingActions = true;
        state.isDeletingActionsError = null;
      })
      .addCase(deleteEntireSection.fulfilled, (state, action) => {
        const { sectionId } = action.payload;

        if (sectionId in state.collapsedSections) {
          const collapsedSections = { ...state.collapsedSections };

          delete collapsedSections[sectionId];
          state.collapsedSections = collapsedSections;
        }
        state.isDeletingActions = false;
        state.isDeletingActionsError = null;
        state.visibleActions = deleteSectionAndContents(state.visibleActions, sectionId);
      })
      .addCase(updateAction.fulfilled, (state, { meta, payload: updatedAction }) => {
        const { new_domain_for_all, old_domain_to_replace, screenshot, user_cropped_screenshot: croppedScreenshot } = meta.arg;

        const aScreenshotWasUploaded = typeof screenshot !== 'undefined' || typeof croppedScreenshot !== 'undefined';

        if (aScreenshotWasUploaded) {
          // Prevent updating the state if it didn't change.
          // Browser caches these, so it's okay to keep them even if they expired.
          if (!state.actionForms[updatedAction.id]) {
            // TODO: This should never happen. Investigate where these are coming from.
            //  Wait for https://linear.app/scribe/issue/DOCS-1113 to be "Done" (and only "Done") to remove the comment.
            state.actionForms[updatedAction.id] = buildActionForm(updatedAction);
          }
          const form = state.actionForms[updatedAction.id];

          form.screenshotState = { loaded: false, errored: false };
          form.values.screenshotUrl = updatedAction.user_cropped_screenshot?.url || updatedAction.screenshot_url;
          form.values.screenshotScale = updatedAction.default_scale_factor;
        }

        state.visibleActions = state.visibleActions.map((action) => {
          if (action.id === updatedAction.id) {
            return updatedAction;
          }
          if (new_domain_for_all && action.url?.startsWith(old_domain_to_replace)) {
            action.url = action.url.replace(old_domain_to_replace, new_domain_for_all);
          }
          return action;
        });
        state.isLoading = false;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(updateAction.pending, (state, action) => {
        const { id, description, instruction_type, user_cropped_screenshot } = action.meta.arg;

        state.visibleActions = state.visibleActions.map((action) => {
          if (action.id !== id) return action;
          return {
            ...action,
            description: description || action.description,
            instruction_type: instruction_type || action.instruction_type,
            isUpdatingScreenshot: !!user_cropped_screenshot
          };
        });
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(updateAction.rejected, (state, action) => {
        const { id } = action.meta.arg;

        const errors = action.error;

        const combineErrors = (...errorArrays) => [...new Set(errorArrays.reduce((output, errorArray) => [...output, ...errorArray], []))];

        state.actionForms[id].errors = {
          detail: errors.detail,
          screenshotUrl: combineErrors(errors.screenshot || [], errors.user_cropped_screenshot || []),
          screenshotScale: errors.default_scale_factor,
          screenshotTranslate: combineErrors(errors.drag_translate_x || [], errors.drag_translate_y || [])
        };

        state.visibleActions = state.visibleActions.map((act) => {
          if (act.id === id) {
            return {
              ...act,
              isUpdatingScreenshot: false,
              isUpdatingError: true
            };
          }
          return act;
        });
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = errorParser(action.payload, 'actionSlice.updateAction');
      })
      .addCase(addLegacyComment.fulfilled, (state, action) => {
        const newComment = action.payload;

        state.visibleActions = state.visibleActions.map((action) => {
          if (action.id === newComment.action.id) {
            if (!action.comments) {
              const comments = [newComment];

              return {
                ...action,
                num_comments: comments.length,
                comments
              };
            }
            const comments = [...action.comments, newComment];

            return {
              ...action,
              num_comments: comments.length,
              comments
            };
          }
          return action;
        });
        state.isLoading = false;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(addLegacyComment.pending, (state, action) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(addLegacyComment.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = errorParser(action.payload, 'actionSlice.addLegacyComment');
      })
      .addCase(deleteCommentFromAction.fulfilled, (state, action) => {
        const { commentedActionId, commentId } = action.payload;

        state.visibleActions = state.visibleActions.map((action) => {
          if (action.id === commentedActionId) {
            const oldComments = action.comments;
            const newComments = oldComments.filter((comment) => comment.id !== commentId);

            return {
              ...action,
              num_comments: newComments ? newComments.length : 0,
              comments: newComments
            };
          }
          return action;
        });
        state.isLoading = false;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(deleteCommentFromAction.pending, (state, action) => {
        state.isLoading = true;
        state.isError = false;
        state.errorMessage = null;
      })
      .addCase(deleteCommentFromAction.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = errorParser(action.payload, 'actionSlice.deleteCommentFromAction');
      })
      .addCase(selectedActionChange, (state, action) => {
        const { actionId, selected } = action.payload;

        state.visibleActions = state.visibleActions.map((action) => {
          if (action.id === actionId) {
            return { ...action, selected };
          }
          return action;
        });
      })
      .addCase(deselectAllActions, (state) => {
        state.visibleActions = state.visibleActions.map((action) => {
          return { ...action, selected: false };
        });
      })
      .addCase(selectUpToAction, (state, action) => {
        const { actionId } = action.payload;
        const indexOfAction = state.visibleActions.findIndex((item) => item.id === actionId);

        if (indexOfAction === -1) {
          return;
        }
        const upToSelected = state.visibleActions.slice(0, indexOfAction);

        upToSelected.reverse();
        const reverseIndex = upToSelected.findIndex((item) => item.selected);
        // lastIndexOfSelected

        if (reverseIndex === -1) {
          return;
        }
        const lastIndex = upToSelected.length - 1 - reverseIndex;

        state.visibleActions = state.visibleActions.map((action, index) => {
          if (index >= lastIndex && index <= indexOfAction) {
            return { ...action, selected: true };
          }
          return action;
        });
      })
      .addCase(selectAllActions, (state, action) => {
        const selected = action.payload;

        state.visibleActions = state.visibleActions.map((action) => ({ ...action, selected }));
      })
      .addCase(selectMultipleActions, (state, action) => {
        const { selected, actionIds } = action.payload;

        state.visibleActions = state.visibleActions.map((action) => {
          if (actionIds.includes(action.id)) {
            return { ...action, selected };
          }
          return action;
        });
      })
      .addCase(deleteSelectedActions.pending, (state) => {
        state.isDeletingActions = true;
        state.isDeletingActionsError = null;
      })
      .addCase(deleteSelectedActions.rejected, (state) => {
        state.isDeletingActionsError = 'Error while trying to delete selected steps';
        state.isDeletingActions = false;
      })
      .addCase(deleteSelectedActions.fulfilled, (state, action) => {
        const idsToDelete = action.payload;

        idsToDelete.forEach((id) => {
          if (id in state.collapsedSections) {
            const collapsedSections = { ...state.collapsedSections };

            delete collapsedSections[id];
            state.collapsedSections = collapsedSections;
          }
        });
        state.isDeletingActions = false;
        state.isDeletingActionsError = null;
        state.visibleActions = [...state.visibleActions].filter((a) => !idsToDelete.includes(a.id));
      })
      .addCase(scribeFileAddAction.fulfilled, (state, action) => {
        // add new created action in scribe file
        const { newAction, addAfterActionId } = action.payload;
        const addAfterIdx = state.visibleActions.findIndex((action) => action.id === addAfterActionId);

        state.visibleActions = [
          ...state.visibleActions.slice(0, addAfterIdx + 1),
          { ...newAction, selected: false },
          ...state.visibleActions.slice(addAfterIdx + 1, state.visibleActions.length)
        ].filter((a) => a.id !== 'newActionPlaceholderId');
        state.actionForms[newAction.id] = buildActionForm(newAction);
        state.count += 1;
        if (newAction.kind !== 'section' && newAction.instruction_type !== 'tip' && newAction.instruction_type !== 'warning') {
          state.countWithoutSections += 1;
        }
        state.isLoading = false;
        state.isError = null;
      })
      .addCase(scribeFileAddAction.pending, (state, action) => {
        const { addAfterActionId, kind, instructionType } = action.meta.arg;
        const newAction = {
          id: 'newActionPlaceholderId',
          kind,
          instruction_type: instructionType,
          description: getTitleByActionKindOrType(kind, instructionType),
          collapsed: false,
          selected: false,
          hidden: false,
          comments: []
        };

        const addAfterIdx = state.visibleActions.findIndex((action) => action.id === addAfterActionId);

        state.visibleActions = [
          ...state.visibleActions.slice(0, addAfterIdx + 1),
          { ...newAction },
          ...state.visibleActions.slice(addAfterIdx + 1, state.visibleActions.length)
        ];
        state.isLoading = true;
        state.isError = null;
      })
      .addCase(scribeFileAddAction.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = errorParser(action.payload, 'actionSlice.scribeFileAddAction');
      })
      .addCase(scribeFileUpdateActions, (state, { payload }) => {
        state.visibleActions = payload.updatedActions;

        state.visibleActions.forEach((action) => {
          if (state.actionForms[action.id]) return;
          state.actionForms[action.id] = buildActionForm(action);
        });

        state.countWithoutSections = payload.updatedActions.filter(
          (docAction) => docAction.kind !== 'section' && docAction.instruction_type !== 'tip' && docAction.instruction_type !== 'warning'
        ).length;
      })
      .addCase(fetchSingleAction.fulfilled, (state, action) => {
        const newAction = action.payload;

        state.visibleActions = state.visibleActions.map((action) => {
          if (action.id === newAction.id) {
            return newAction;
          }
          return action;
        });
      })
      .addCase(stackSelectedActions.fulfilled, (state, action) => {
        const { stack, actionIdsToStack, updatedStack, selectedSections } = action.payload;
        const stackedActionsCount = actionIdsToStack.length - selectedSections.length;

        if (updatedStack) {
          const updatedActions = updateStackActions(state.backupActions, stack, actionIdsToStack);

          state.visibleActions = updatedActions;
        } else {
          const updatedActions = addCreatedStackToActions(state.backupActions, stack, actionIdsToStack);

          state.visibleActions = updatedActions;
        }
        const numRemovedActions = stackedActionsCount - 1;

        state.count -= numRemovedActions;
        // Sections are never added to stacks
        state.countWithoutSections -= numRemovedActions;
        state.backupActions = [];
        state.errorMessage = null;
      })
      .addCase(stackSelectedActions.pending, (state) => {
        state.backupActions = [...state.visibleActions];
        state.visibleActions = optimisticStackUpdate(state.visibleActions);
        state.errorMessage = null;
      })
      .addCase(stackSelectedActions.rejected, (state, action) => {
        // Revert the Optimistic Stack Update
        // Optimize?
        state.visibleActions = [...state.backupActions];
        state.errorMessage = errorParser(action.payload, 'actionSlice.stackSelectedActions');
      })
      .addCase(deleteStackedActions.fulfilled, (state, action) => {
        const { stack } = action.payload;
        const stackedActionsCount = stack.stack_actions.length;

        state.visibleActions = unstackActions(state.visibleActions, stack);
        const numAddedActions = stackedActionsCount - 1;

        state.count += numAddedActions;
        // Sections are never added to stacks
        state.countWithoutSections += numAddedActions;
        state.isUnstackingActionsError = null;
        state.isUnstackingActions = false;
      })
      .addCase(deleteStackedActions.pending, (state) => {
        state.isUnstackingActionsError = null;
        state.isUnstackingActions = true;
      })
      .addCase(deleteStackedActions.rejected, (state) => {
        state.isUnstackingActionsError = 'Error while splitting stacked steps';
        state.isUnstackingActions = false;
      })
      .addCase(deleteAutoStackedActions.rejected, (state) => {
        state.isUnstackingActionsError = 'Error while splitting auto combined steps';
        state.isUnstackingActions = false;
      })
      .addCase(clearDeletingErrors, (state) => {
        state.isDeletingActionsError = null;
      })
      .addCase(deleteAutoStackedActions.pending, (state, action) => {
        state.isUnstackingActionsError = null;
        state.isUnstackingActions = true;
      })
      .addCase(deleteAutoStackedActions.fulfilled, (state) => {
        state.isUnstackingActionsError = null;
        state.isUnstackingActions = false;
      })
      .addCase(clearUnstackingErrors, (state) => {
        state.isUnstackingActionsError = null;
      })
      .addCase(reorderActions.fulfilled, (state, action) => {
        const currentNumVisibleActions = state.visibleActions.length;
        const reorderedActions = action.payload;
        const reorderedActionsWithHiddenAndCollapsed = reorderedActions.map((action) => {
          if (state.hiddenActions[action.id]) {
            return {
              ...action,
              hidden: true
            };
          }
          if (state.collapsedSections[action.id]) {
            return {
              ...action,
              collapsed: true
            };
          }
          return action;
        });

        state.visibleActions = sortBy(reorderedActionsWithHiddenAndCollapsed.slice(0, currentNumVisibleActions), ['sort_order']);
        // state.visibleActions = updateHidden(sortBy(reorderedActions.slice(0, currentNumVisibleActions), ['sort_order']), state.collapsedSections, state.hiddenActions);
        state.isLoading = false;
        state.isError = null;
        state.errorMessage = null;
      })
      .addCase(reorderActions.pending, (state) => {
        state.isLoading = true;
        state.isError = null;
        state.errorMessage = null;
      })
      .addCase(reorderActions.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.errorMessage = errorParser(action.payload, 'actionSlice.reorderActions');
      })
      .addCase(collapseSection, (state, action) => {
        const { collapsedSectionId } = action.payload;

        state.collapsedSections[collapsedSectionId] = true;
        const currentActions = state.visibleActions;

        state.visibleActions = updateHidden(currentActions, state.collapsedSections, state.hiddenActions);
      })
      .addCase(expandSection, (state, action) => {
        const { expandedSectionId } = action.payload;

        state.collapsedSections[expandedSectionId] = false;
        const currentActions = state.visibleActions;

        state.visibleActions = updateHidden(currentActions, state.collapsedSections, state.hiddenActions);
      })
      .addCase(scribeFileGetActionsSortOrder.fulfilled, (state, action) => {
        const actions = action.payload;
        const updatedSortOrder = {};

        actions.forEach(function (action) {
          updatedSortOrder[action.id] = action.sort_order;
        });

        state.visibleActions = state.visibleActions.map((action) => {
          if (updatedSortOrder[action.id]) {
            return {
              ...action,
              sort_order: updatedSortOrder[action.id]
            };
          }
          return action;
        });

        state.isLoading = false;
        state.isError = null;
      })
      .addCase(scribeFileGetActionsSortOrder.pending, (state, action) => {
        state.isLoading = true;
        state.isError = null;
      })
      .addCase(scribeFileGetActionsSortOrder.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = errorParser(action.payload, 'actionSlice.scribeFileGetActionsSortOrder');
      })
      .addCase('signOutGlobally', () => ({ ...INITIAL_STATE }));
  }
});

export const {
  setNewScreenshotModalOpen,
  setStepRecorderState,
  setActionFormErrors,
  setActionFormValues,
  setActionFormScreenshotState,
  setActionForms,
  setRecorderActionForms
} = actionsSlice.actions;

export const selectVisibleActions = createSelector(
  [
    (state) => state.actions.scribeDocumentId,
    (state) => state?.actions?.visibleActions,
    (state) => state.actions.count,
    (state) => state.actions.countWithoutSections,
    (state) => state.actions.autoStackCount,
    (state) => state.actions.isError,
    (state) => state.actions.errorMessage,
    (state) => state.actions.isLoading,
    (state) => state.actions.initialClientSideActionLoadComplete
  ],
  (
    visibleActionScribeId,
    visibleActions,
    totalActionCount,
    actionsWithoutSectionsCount,
    autoStackCount,
    isErrorActions,
    errorMessage,
    isLoadingActions,
    initialClientSideActionLoadComplete
  ) => ({
    visibleActionScribeId,
    visibleActions,
    totalActionCount,
    actionsWithoutSectionsCount,
    autoStackCount,
    isErrorActions,
    errorMessage,
    isLoadingActions,
    initialClientSideActionLoadComplete
  })
);

export const selectActionForm = createSelector(
  [(state, actionId) => state.actions.actionForms[actionId]],
  (form) => form || getEmptyActionForm()
);

export const selectActionFormScreenshotErrors = createSelector(
  [(state, actionId) => state.actions.actionForms[actionId]?.errors.screenshotUrl],
  (form) => form || []
);

export const selectIsUpdatingAction = (state) => state.actions.isLoading;
export const selectIsNewScreenshotModalOpen = (actionId) => (state) => !!state.actions.actionForms[actionId]?.isAddScreenshotModalOpen;
export const selectStepRecorderState = (state) => state.actions.stepRecorderState;
export const selectDeletingActions = createSelector(
  [(state) => state.actions.isDeletingActions, (state) => state.actions.isDeletingActionsError],
  (isDeletingActions, isDeletingActionsError) => ({ isDeletingActions, isDeletingActionsError })
);

export const selectStackingActions = createSelector(
  [(state) => state.actions.isUnstackingActions, (state) => state.actions.isUnstackingActionsError],
  (isUnstackingActions, isUnstackingActionsError) => ({ isUnstackingActions, isUnstackingActionsError })
);

/**
 * @typedef {Object} SelectedActionsSubState
 * @property {Array<import('scribe-web-shared/types/document').Action>} allActions
 * @property {Array<import('scribe-web-shared/types/document').Action>} selectedActions
 * @property {Array<string>} selectedIds
 * @property {number} firstSelectedAction
 * @property {number} lastSelectedAction
 * @property {number} numSelectedActions
 */

export const selectSelectedActions = createSelector(
  [(state) => state?.actions?.visibleActions],
  /**
   * @returns {SelectedActionsSubState}
   */
  (visibleActions) => {
    const allActions = visibleActions ?? [];
    const selectedActions = visibleActions?.filter((item) => item.selected) ?? [];
    const selectedIds = selectedActions.flatMap((action) => {
      if (action.kind === 'section' && action.collapsed) {
        return [action.id, ...getSectionContents(allActions, action.id)];
      }

      return [action.id];
    });

    return {
      selectedActions,
      selectedIds,
      allActions,
      firstSelectedAction: visibleActions.findIndex((item) => item.selected) ?? 0,
      lastSelectedAction: findLastIndex(visibleActions, (item) => item.selected) ?? 0,
      numSelectedActions: selectedActions.length
    };
  }
);

export default actionsSlice.reducer;
